/**
 * O nome do papel em português, para a tela.
 *
 * ── Por que existe ────────────────────────────────────────────────────────
 *
 * `admin_tenant` é o slug de `core.roles` — um identificador, escrito para o
 * banco e para o código. Ele estava chegando cru na tela, ao lado do nome da
 * organização e na ficha da sessão, e quem lê não escreve código.
 *
 * O slug continua sendo a verdade em tudo que não é tela: contrato, RBAC,
 * banco, log. A tradução acontece no último passo, e só ali.
 *
 * ── Um mapa, e não um `replace('_', ' ')` ─────────────────────────────────
 *
 * A regra mecânica produziria "admin tenant" e "operacao cliqfy", que são o
 * mesmo jargão com espaço no meio. O que a pessoa precisa ler é o papel dela
 * na organização — e "Administrador" não é tradução de `admin_tenant`, é o
 * nome daquilo.
 *
 * Papel desconhecido devolve o próprio slug: um papel novo aparece feio, o que
 * é visível e conserta-se; devolver vazio o esconderia.
 *
 * ── Este é o ÚNICO mapa de papel da web ───────────────────────────────────
 *
 * A tela de entrada tinha o dela, com outro vocabulário ("Vendas", "Gestão",
 * "Atendimento"): nomes de ÁREA, enquanto estes são nomes de PESSOA. Dois
 * mapas para a mesma pergunta divergem — e divergiram, porque um deles não
 * recebeu a mudança abaixo. Quem precisa do nome do papel chama `nomeDoPapel`.
 */
export const NOMES_DE_PAPEL: Readonly<Record<string, string>> = {
  agente: 'Atendente',
  /*
   * Era "Vendedor".
   *
   * O papel descreve alcance — a própria carteira: conversas, clientes e
   * oportunidades dela —, e quem faz esse trabalho numa clínica é a recepção
   * ou a secretária. "Vendedor" nomeava o cargo de um ramo só, e obrigaria
   * toda organização que não vende carro a traduzir mentalmente o que leu.
   *
   * `vendedor` continua sendo o valor no contrato, no RBAC e no banco. Mudar o
   * enum custaria migração para ganho nenhum — e a régua do ADR-0016 é que o
   * identificador é nosso, o rótulo é de quem lê.
   */
  vendedor: 'Responsável por carteira',
  gestor: 'Gestor',
  admin_tenant: 'Administrador',
  /*
   * O papel de quem opera a plataforma, e não a organização do cliente.
   *
   * "Suporte Cliqfy" e não "Operação": quem vê este papel numa tela de cliente
   * é alguém da Cliqfy dando suporte, e é isso que a palavra precisa dizer para
   * quem estiver olhando por cima do ombro.
   */
  operacao_cliqfy: 'Suporte Cliqfy',
  sistema: 'Sistema',
  // ADR-0025: a equipe da Cliqfy que faz tudo, em todas as organizações.
  super_admin: 'Super Admin',
};

export function nomeDoPapel(papel: string | null | undefined): string {
  if (!papel) return '';
  return NOMES_DE_PAPEL[papel] ?? papel;
}
